import React, { useContext, useMemo, useState } from 'react'
import { Box, Chip, Stack, Tooltip, Typography } from '@mui/material'
import PeopleIcon from '@mui/icons-material/People'
import AltRouteIcon from '@mui/icons-material/AltRoute'
import RouteIcon from '@mui/icons-material/Route'
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline'
import LocalHospitalRoundedIcon from '@mui/icons-material/LocalHospitalRounded'
import AccessTimeIcon from '@mui/icons-material/AccessTime'
import MyLocationIcon from '@mui/icons-material/MyLocation'
import LocationDisabledIcon from '@mui/icons-material/LocationDisabled'
import { PTSControlContext } from './PTSControl'
import moment from 'moment'
import API from '../../../services/API'

const consentChipStyles = {
  Approved: { color: '#86EFAC', label: 'sharing' },
  Pending: { color: '#FDE68A', label: 'asked' },
  Declined: { color: '#FCA5A5', label: 'declined' },
  Expired: { color: '#FDBA74', label: 'expired' },
}

const TERMINAL_JOURNEY_STATUSES = [
  'Completed',
  'Cancelled',
  'No Show',
  'Aborted',
]

const STARTED_JOURNEY_STATUSES = [
  'Mobile',
  'On Scene',
  'Patient Onboard',
  'Transporting',
  'Arrived',
  'Handover Complete',
  'Completed',
]

const PATIENT_ON_BOARD_STATUSES = ['Patient Onboard', 'Transporting']

const getJourneyPatientID = (journey) =>
  journey?.booking?.patient?._id || journey?.patient

const shiftIsCovered = (shift) =>
  !!shift?.assignedTo ||
  !!shift?.assignedSubcontractor ||
  !!shift?.assignedToSubcontractor

export const getStatusColorStyles = (status) => {
  switch (status) {
    case 'Available':
      return {
        bg: 'rgba(34,197,94,0.16)',
        border: 'rgba(34,197,94,0.35)',
        color: '#86EFAC',
        label: 'AVAILABLE',
      }
    case 'En Route':
    case 'Transporting':
      return {
        bg: 'rgba(59,130,246,0.16)',
        border: 'rgba(59,130,246,0.35)',
        color: '#93C5FD',
        label: status.toUpperCase(),
      }
    case 'At Pickup':
    case 'At Dropoff':
      return {
        bg: 'rgba(245,158,11,0.16)',
        border: 'rgba(245,158,11,0.35)',
        color: '#FCD34D',
        label: status.toUpperCase(),
      }
    case 'Unavailable':
      return {
        bg: 'rgba(239,68,68,0.16)',
        border: 'rgba(239,68,68,0.35)',
        color: '#FCA5A5',
        label: 'UNAVAILABLE',
      }
    case 'Crew Logging On':
      return {
        bg: 'rgba(245,158,11,0.14)',
        border: 'rgba(245,158,11,0.32)',
        color: '#FCD34D',
        label: 'CREW LOGGING ON',
      }
    case 'Off Duty':
      return {
        bg: 'rgba(148,163,184,0.16)',
        border: 'rgba(148,163,184,0.3)',
        color: '#CBD5E1',
        label: 'OFF DUTY',
      }
    default:
      return {
        bg: 'rgba(148,163,184,0.16)',
        border: 'rgba(148,163,184,0.3)',
        color: '#E2E8F0',
        label: (status || 'PLANNED').toUpperCase(),
      }
  }
}

export const deriveDutyDisplayStatus = ({
  duty,
  allJourneys,
  patientsOnBoardCount,
}) => {
  if (['Completed', 'Cancelled', 'Unavailable'].includes(duty?.status)) {
    return duty.status
  }

  if (patientsOnBoardCount > 0) return 'Transporting'

  const statuses = allJourneys.map((journey) => journey?.status)
  if (
    statuses.some((status) => ['Arrived', 'Handover Complete'].includes(status))
  ) {
    return 'At Dropoff'
  }
  if (statuses.includes('On Scene')) return 'At Pickup'
  if (
    statuses.some((status) => ['Mobile', 'Crew Acknowledged'].includes(status))
  ) {
    return 'En Route'
  }

  const activeShiftCount =
    (duty?.crewShifts ?? []).length
      ? (duty?.crewShifts ?? []).filter(
          (shift) => shift?.clock?.startedAt && !shift?.clock?.endedAt,
        ).length
      : duty?.activeShiftCount ?? 0
  const expectedCrewCount =
    (duty?.crewShifts ?? []).length
      ? (duty?.crewShifts ?? []).filter((shift) => !!shift?.assignedTo).length
      : duty?.clockRequiredShiftCount ?? duty?.filledShiftCount ?? 0

  // 'Off Duty' only makes sense when someone is actually expected to clock
  // in. Subcontracted crews (no employee account, can never clock in) and
  // crewless duties read as 'Planned' instead of a permanent OFF DUTY.
  if (!activeShiftCount) return expectedCrewCount > 0 ? 'Off Duty' : 'Planned'
  if (activeShiftCount < expectedCrewCount) {
    return 'Crew Logging On'
  }
  return 'Available'
}

const StatBox = ({ value, label, accent, icon }) => (
  <Tooltip title={label} arrow placement="top">
    <Box
      aria-label={`${value} ${label}`}
      sx={{
        minWidth: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 0.35,
        color: accent,
      }}
    >
      <Box
        sx={{
          width: 18,
          height: 18,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        {icon}
      </Box>
      <Typography
        sx={{
          fontSize: '1rem',
          fontWeight: 900,
          lineHeight: 1,
          minWidth: 10,
          textAlign: 'center',
          color: 'whitesmoke',
        }}
      >
        {value}
      </Typography>
    </Box>
  </Tooltip>
)

export const DutyVehicleImage = ({ duty }) => {
  const [errored, setErrored] = useState(false)

  const imageSrc = duty?.vehicle?.imageUrl || duty?.vehicle?.image

  const showImage = !!imageSrc && !errored

  return (
    <Box
      sx={{
        width: 132,
        height: 92,
        borderRadius: 2.5,
        overflow: 'hidden',
        position: 'relative',
        flexShrink: 0,
        background:
          'linear-gradient(180deg, rgba(15,23,42,0.95) 0%, rgba(2,6,23,1) 100%)',
        border: '1px solid rgba(255,255,255,0.08)',
        boxShadow:
          '0 10px 20px rgba(0,0,0,0.25), inset 0 1px 0 rgba(255,255,255,0.04)',
      }}
    >
      {showImage ? (
        <Box
          component="img"
          src={imageSrc}
          alt={duty?.vehicle?.callSign || 'Vehicle'}
          onError={() => setErrored(true)}
          sx={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            display: 'block',
            filter: 'saturate(1.02) contrast(1.02)',
          }}
        />
      ) : (
        <Stack
          alignItems="center"
          justifyContent="center"
          sx={{ width: '100%', height: '100%' }}
        >
          <LocalHospitalRoundedIcon
            sx={{ fontSize: 34, color: 'rgba(147,197,253,0.9)' }}
          />
          <Typography
            sx={{
              mt: 0.5,
              color: 'rgba(248,250,252,0.82)',
              fontSize: '0.72rem',
              fontWeight: 700,
              letterSpacing: 0.5,
            }}
          >
            NO IMAGE
          </Typography>
        </Stack>
      )}

      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          background:
            'linear-gradient(90deg, rgba(2,6,23,0.08) 0%, rgba(2,6,23,0.18) 38%, rgba(2,6,23,0.42) 100%)',
        }}
      />
    </Box>
  )
}

const DutyCard = ({ duty }) => {
  const { selectedDuty, setSelectedDuty, setSelectedRoute, refreshControl } =
    useContext(PTSControlContext)
  const [requestingTrackingFor, setRequestingTrackingFor] = useState(null)

  // Ask a crew member (again) to share the vehicle's live position — used
  // after an accidental decline or an expired approval.
  const requestTracking = (event, consent) => {
    event.stopPropagation()
    if (requestingTrackingFor) return
    setRequestingTrackingFor(String(consent.user))
    API.post(`/pts/controlroom/duties/${duty._id}/request-tracking`, {
      userID: consent.user,
    })
      .then((res) => {
        if (res.status === 200) refreshControl?.()
      })
      .finally(() => setRequestingTrackingFor(null))
  }

  const isSelected = selectedDuty?._id === duty?._id

  const allJourneys = useMemo(
    () =>
      (duty?.routes ?? [])
        .flatMap((route) => route?.journeys ?? [])
        .filter(Boolean),
    [duty],
  )

  const remainingJourneyCount = useMemo(() => {
    return allJourneys.filter(
      (journey) => !TERMINAL_JOURNEY_STATUSES.includes(journey?.status),
    ).length
  }, [allJourneys])

  const notStartedRouteCount = useMemo(() => {
    return (duty?.routes ?? []).filter((route) => {
      const routeJourneys = (route?.journeys ?? []).filter(Boolean)
      const hasOpenJourney = routeJourneys.some(
        (journey) => !TERMINAL_JOURNEY_STATUSES.includes(journey?.status),
      )
      const hasStartedJourney = routeJourneys.some((journey) =>
        STARTED_JOURNEY_STATUSES.includes(journey?.status),
      )

      return hasOpenJourney && !hasStartedJourney
    }).length
  }, [duty?.routes])

  const patientsOnBoardCount = useMemo(() => {
    return new Set(
      allJourneys
        .filter((journey) =>
          PATIENT_ON_BOARD_STATUSES.includes(journey?.status),
        )
        .map(getJourneyPatientID)
        .filter(Boolean)
        .map(String),
    ).size
  }, [allJourneys])

  const displayStatus = deriveDutyDisplayStatus({
    duty,
    allJourneys,
    patientsOnBoardCount,
  })
  const statusStyles = getStatusColorStyles(displayStatus)
  const crewMessagesAwaitingReply =
    duty?.crewChatMessagesAwaitingReply ??
    duty?.crewChatMessagesSinceLastControlCount ??
    0
  const hasUnreadCrewChat = (duty?.unreadCrewChatCount || 0) > 0
  const earliestCrewEnd = useMemo(() => {
    if (duty?.earliestCrewEnd) return duty.earliestCrewEnd

    const crewEnds = (duty?.crewShifts ?? [])
      .map((shift) => shift?.end)
      .filter(Boolean)

    if (!crewEnds.length) return null

    return crewEnds.reduce((earliest, value) =>
      moment(value).isBefore(moment(earliest)) ? value : earliest,
    )
  }, [duty?.crewShifts, duty?.earliestCrewEnd])

  return (
    <Box
      onClick={() => {
        setSelectedRoute?.(null)
        setSelectedDuty(isSelected ? null : duty)
      }}
      sx={{
        cursor: 'pointer',
        p: 1,
        borderRadius: 2,
        overflow: 'hidden',
        background: isSelected
          ? 'linear-gradient(180deg, rgba(30,41,59,0.98) 0%, rgba(15,23,42,1) 100%)'
          : 'linear-gradient(180deg, rgba(17,24,39,0.98) 0%, rgba(15,23,42,0.98) 100%)',
        border: isSelected
          ? '1px solid rgba(96,165,250,0.45)'
          : '1px solid rgba(255,255,255,0.07)',
        boxShadow: isSelected
          ? `
            0 14px 30px rgba(0,0,0,0.3),
            0 0 0 1px rgba(59,130,246,0.12),
            inset 0 1px 0 rgba(255,255,255,0.05)
          `
          : `
            0 8px 18px rgba(0,0,0,0.22),
            inset 0 1px 0 rgba(255,255,255,0.03)
          `,
        transition: 'all 0.18s ease',
        '&:hover': {
          transform: 'translateY(-2px)',
          borderColor: 'rgba(148,163,184,0.22)',
        },
      }}
    >
      <Stack spacing={0.9}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          spacing={1}
        >
          <Box minWidth={0}>
            <Typography
              noWrap
              sx={{
                color: duty?.vehicle?.callSign ? '#EAF2FF' : '#FDE68A',
                fontWeight: 900,
                fontSize: '1.15rem',
                lineHeight: 1,
                letterSpacing: 0.2,
              }}
            >
              {duty?.vehicle?.callSign ?? 'NO VEHICLE'}
            </Typography>
          </Box>

          <Chip
            label={statusStyles.label}
            size="small"
            sx={{
              height: 26,
              maxWidth: 138,
              fontWeight: 850,
              letterSpacing: 0,
              color: statusStyles.color,
              backgroundColor: statusStyles.bg,
              border: `1px solid ${statusStyles.border}`,
              borderRadius: 1,
              '& .MuiChip-label': {
                px: 0.9,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              },
            }}
          />
        </Stack>

        <Stack
          direction="row"
          spacing={1.15}
          alignItems="center"
          justifyContent="space-between"
        >
          <Stack direction="row" spacing={1.15} alignItems="center">
            <StatBox
              value={remainingJourneyCount}
              label={`${remainingJourneyCount === 1 ? 'Journey' : 'Journeys'} Remaining`}
              accent="#86EFAC"
              icon={<RouteIcon sx={{ fontSize: 16 }} />}
            />
            <StatBox
              value={notStartedRouteCount}
              label={`${notStartedRouteCount === 1 ? 'Route' : 'Routes'} Not Started`}
              accent="#FCD34D"
              icon={<AltRouteIcon sx={{ fontSize: 16 }} />}
            />
            <StatBox
              value={patientsOnBoardCount}
              label={`${patientsOnBoardCount === 1 ? 'Patient' : 'Patients'} on board`}
              accent="#F8FAFC"
              icon={<PeopleIcon sx={{ fontSize: 16 }} />}
            />
            {earliestCrewEnd && (
              <StatBox
                value={moment(earliestCrewEnd).format('HH:mm')}
                label="Earliest crew finish"
                accent="#93C5FD"
                icon={<AccessTimeIcon sx={{ fontSize: 16 }} />}
              />
            )}
          </Stack>
          {(duty?.trackingConsents ?? []).length > 0 && (
            <Stack direction="row" spacing={0.5} alignItems="center">
              {(duty?.trackingConsents ?? []).map((consent) => {
                const style =
                  consentChipStyles[consent.status] ?? consentChipStyles.Pending
                const canReask = consent.status !== 'Approved'
                return (
                  <Tooltip
                    key={String(consent.user)}
                    arrow
                    title={
                      consent.status === 'Approved'
                        ? `${consent.name} is sharing location${consent.until ? ` until ${moment(consent.until).format('HH:mm')}` : ''} (or clock-out)`
                        : `${consent.name}: location sharing ${style.label}${canReask ? ' — click to ask again' : ''}`
                    }
                  >
                    <Box
                      onClick={
                        canReask
                          ? (event) => requestTracking(event, consent)
                          : undefined
                      }
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.35,
                        color: style.color,
                        fontWeight: 900,
                        fontSize: '0.72rem',
                        cursor: canReask ? 'pointer' : 'default',
                        opacity:
                          requestingTrackingFor === String(consent.user)
                            ? 0.4
                            : 1,
                      }}
                    >
                      {consent.status === 'Approved' ? (
                        <MyLocationIcon sx={{ fontSize: 14 }} />
                      ) : (
                        <LocationDisabledIcon sx={{ fontSize: 14 }} />
                      )}
                      {consent.name?.split(' ')[0]}
                    </Box>
                  </Tooltip>
                )
              })}
            </Stack>
          )}
          {crewMessagesAwaitingReply > 0 && (
            <Tooltip
              arrow
              title={
                duty.latestCrewChatMessage?.message ||
                `${crewMessagesAwaitingReply} crew messages awaiting reply`
              }
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.35,
                  color: '#93C5FD',
                  fontWeight: 900,
                  fontSize: '0.78rem',
                  animation: hasUnreadCrewChat
                    ? 'ptsChatPulse 1.1s ease-in-out infinite'
                    : 'none',
                  '@keyframes ptsChatPulse': {
                    '0%, 100%': {
                      opacity: 1,
                      transform: 'scale(1)',
                      color: '#FCD34D',
                    },
                    '50%': {
                      opacity: 0.38,
                      transform: 'scale(1.12)',
                      color: '#F59E0B',
                    },
                  },
                }}
              >
                <ChatBubbleOutlineIcon sx={{ fontSize: 15 }} />
                {crewMessagesAwaitingReply}
              </Box>
            </Tooltip>
          )}
        </Stack>
      </Stack>
    </Box>
  )
}

export default DutyCard
